---
title: "Predicting churn from product signals you already *have*."
excerpt: "Seat dormancy, support volume, and payment friction beat any single health score. A model you can ship this quarter."
category: "Churn"
author: samuel
date: "2026-04-03"
readTime: "9 min read"
deck: "You don't need a data science team to see churn coming. The *signals* are already in your product data."
---

Churn prediction has a reputation for requiring a machine learning team and a year of
labeled data. It doesn't. Most of the warning signs that a customer is about to leave are
already sitting in the product and billing data you collect today, and a handful of them,
combined sensibly, outperform the single composite "health score" that most tools sell you.
The value isn't a clever model. It's noticing in time to act.

## The signals that actually predict churn

Three categories of signal carry most of the predictive weight, and all three are
observable without new instrumentation:

- **Seat dormancy.** Licensed seats that stop logging in are the clearest leading
  indicator. A team that bought ten seats and uses three is already half-churned.
- **Support friction.** A spike in tickets, or a long-unresolved issue, predicts churn
  far better than a satisfaction survey nobody fills in.
- **Payment friction.** Repeated failed charges, downgrade requests, and invoice
  disputes are churn announcing itself in the billing system.

## Why a single health score hides more than it shows

Composite health scores feel reassuring because they collapse everything into one number.
That's exactly the problem: a "green" account can be a power user on a failing card, and
the score averages the warning away. Watching the underlying signals separately keeps the
specific risk visible, and tells you which intervention to reach for.

> A health score tells you a customer is fine. The raw signals tell you which customer
> to call this afternoon.

## A model you can ship this quarter

Start simple. Define a small set of risk signals, weight them by how strongly each has
preceded churn in your own history, and surface the accounts that trip more than one.
You don't need probabilities to four decimal places. You need a ranked list of accounts
worth a human's attention, refreshed often enough to act before the renewal, not after.
